



































































import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { AttachmentType } from '@getaccept/lib-shared/src/attachments/types/attachment-type';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import ThemedButton from '@getaccept/lib-shared/src/components/ThemedButton.vue';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { IconHelper } from '../../helpers/icon.helper';
import { DURATION } from '../../common/constants/tooltip';

@Component({
  components: {
    ThemedButton,
  },
})
export default class DocumentAttachmentItem extends Vue {
  @Prop() documentAttachment!: DocumentAttachment;
  @Prop() focusedRequiredId?: string;
  @Prop() theme: SigningTheme;
  @Prop() isUploading: boolean;
  @Prop() isSigned: boolean;
  @Prop() isPreview!: boolean;

  focused = false;
  showPreviewTooltip = false;
  hovered = false;

  @Watch('focusedRequiredId')
  watchFocusedRequiredId(focusedRequiredId: string) {
    if (this.documentAttachment.id === focusedRequiredId) {
      this.setFocus(true);
    } else {
      this.setFocus(false);
    }
  }

  get showDescription(): boolean {
    if (this.documentAttachment.attachment.type !== AttachmentType.Upload) {
      return this.documentAttachment.requireView && !this.documentAttachment.lastView;
    }
    return !!this.documentAttachment.attachment.description;
  }

  get showInteraction(): boolean {
    if (this.documentAttachment.attachment.type === AttachmentType.Upload) {
      return !!this.documentAttachment.lastUpload;
    }
    return !!this.documentAttachment.lastView;
  }

  get description(): string {
    return this.documentAttachment.attachment.description;
  }

  get requiredMessage(): string {
    if (this.isPreview || !this.documentAttachment.requireView) {
      return null;
    }
    return this.documentAttachment.attachment.type === AttachmentType.Upload
      ? this.$t('required-upload').toString()
      : this.$t('requires-view').toString();
  }

  get showTooltip(): boolean {
    if (!this.requiredMessage) {
      return false;
    }
    if (this.documentAttachment.lastView || this.documentAttachment.lastUpload) {
      return false;
    }
    return this.focused;
  }

  get attachmentInteraction(): string {
    if (this.documentAttachment.attachment.type === AttachmentType.Upload) {
      return `${this.$t('uploaded-file')}: ${this.documentAttachment.attachment.filename}, ${moment(
        this.documentAttachment.lastUpload
      ).format('L')}`;
    }
    return `${this.$t('viewed-at')} ${moment(this.documentAttachment.lastView).format(
      'hh:mm A L'
    )}`;
  }

  get icon(): string {
    switch (this.documentAttachment.attachment.type) {
      case AttachmentType.Upload:
        return 'upload';
      case AttachmentType.External:
        return 'link';
      default:
        return IconHelper.getFileIcon(this.documentAttachment.attachment.filename);
    }
  }

  get title(): string {
    if (this.documentAttachment.attachment.title) {
      return this.documentAttachment.attachment.title;
    }
    return this.$t('attachment-title-not-found').toString();
  }

  get buttonText(): string {
    if (this.documentAttachment.attachment.type === AttachmentType.Upload && !this.isSigned) {
      return !this.documentAttachment.lastUpload
        ? this.$t('upload').toString()
        : this.$t('replace').toString();
    }
    return this.$t('show').toString();
  }

  get disabled(): boolean {
    return this.isPreview && this.documentAttachment.attachment.type === AttachmentType.Upload;
  }

  handleClick(documentAttachment: DocumentAttachment) {
    if (this.isUploading) {
      return;
    }
    const { id: documentAttachmentId, previewUrl, attachment } = documentAttachment;
    const { id, type, title } = attachment;
    switch (type) {
      case AttachmentType.Upload:
        if (this.isSigned) {
          this.openAttachmentInNewTab(documentAttachmentId, previewUrl);
          return;
        }
        if (this.isPreview) {
          this.togglePreviewTooltip();
          return;
        }
        this.$emit('uploadFile', {
          attachmentId: id,
          name: title,
        });
        break;
      case AttachmentType.External:
      case AttachmentType.File:
      case AttachmentType.Document:
        if (previewUrl) {
          this.openAttachmentInNewTab(documentAttachmentId, previewUrl);
        }
    }
  }

  openAttachmentInNewTab(id: string, url: string) {
    window.open(url);
    this.$emit('attachmentView', id);
  }

  setFocus(hasFocus: boolean) {
    this.focused = hasFocus;
  }

  setHover(hasHover: boolean) {
    this.hovered = hasHover;
  }

  togglePreviewTooltip(): void {
    if (this.showPreviewTooltip) {
      return;
    }
    this.showPreviewTooltip = true;
    setTimeout(() => {
      this.showPreviewTooltip = false;
    }, DURATION);
  }
}
